import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Breadcrumb, Button, Confirm, Form, Header, Message, Search, Segment } from "semantic-ui-react";
import { INVITE_USER, FIND_USERS, ADMIN_GET_SUBSCRIPTION, DELETE_STUDENT_REPORT } from "../../GraphQL";
import { useMutation, useLazyQuery } from "@apollo/client";
import debounce from "lodash/debounce";
import { validate as uuidValidate } from "uuid";

const UserSearchSection = () => {
  const [getUsers, { loading, data }] = useLazyQuery(FIND_USERS);

  const debouncedGetUsers = debounce((e, { value }) => {
    getUsers({
      variables: { filter: value.trim() },
      fetchPolicy: "network-only",
    });
  }, 1000);
  return (
    <Segment basic>
      <Header as="h3">
        User Search
        <Header.Subheader>
          Search by email address, Stripe Customer ID, or User ID
        </Header.Subheader>
      </Header>
      <Search
        id="user-search"
        loading={loading}
        placeholder="janedoe@gmail.com"
        onSearchChange={debouncedGetUsers}
        results={
          data && data.users
            ? data.users.map(({ name, email, sub }) => ({
                as: "a",
                target: "_blank",
                href: `/admin/users/${sub}`,
                title: name,
                description: email,
                id: sub,
              }))
            : []
        }
      />
    </Segment>
  );
};

const SubscriptionSearchSection = () => {
  const [getSubscription, { loading, data }] = useLazyQuery(
    ADMIN_GET_SUBSCRIPTION
  );

  const debouncedGetSubscription = debounce((e, { value }) => {
    getSubscription({
      variables: { id: value.trim() },
      fetchPolicy: "network-only",
    });
  }, 1000);
  return (
    <Segment basic>
      <Header as="h3">
        Subscription Search
        <Header.Subheader>Search by Subscription ID</Header.Subheader>
      </Header>
      <Search
        id="subscription-search"
        loading={loading}
        placeholder="sub_XXX"
        onSearchChange={debouncedGetSubscription}
        results={
          data && data.subscription
            ? [
                {
                  as: "a",
                  target: "_blank",
                  href: `/admin/subscriptions/${data.subscription.id}`,
                  title: data.subscription.id,
                  id: data.subscription.id,
                },
              ]
            : []
        }
      />
    </Segment>
  );
};

const NewSubscriptionSection = () => {
  return (
    <Segment basic>
      <Header as="h3">New Subscription</Header>
      <Button as={Link} to="/admin/subscriptions/new">
          Create New Subscription
      </Button>
    </Segment>
  );
};


const UserInvitationSection = () => {
  const [inviteUser, { loading }] = useMutation(INVITE_USER);
  const [email, setEmail] = useState("");
  const [subscriptionId, setSubscriptionId] = useState("");

  return (
    <Segment basic>
      <Form
        onSubmit={() => {
          inviteUser({
            variables: {
              group: "Teachers",
              email,
              subscription: subscriptionId,
            },
            inviteUser,
          });
        }}
      >
        <Header as="h3">Invite User</Header>
        <Form.Input
          label="Email Address"
          value={email}
          placeholder="janedoe@gmail.com"
          onChange={(e, { value }) => setEmail(value)}
        />
        <Form.Input
          label="Stripe Subscription ID (optional)"
          value={subscriptionId}
          placeholder="sub_XXXXX"
          onChange={(e, { value }) => setSubscriptionId(value)}
        />
        <Form.Button loading={loading} disabled={!email}>
          Invite
        </Form.Button>
      </Form>
    </Segment>
  );
};

const DeleteReportSection = () => {
  const [deleteReport, { loading }] = useMutation(DELETE_STUDENT_REPORT);
  const [reportId, setReportId] = useState("");
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleDelete = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    deleteReport({
      variables: { id: reportId },
      onCompleted: (data) => {
        if (data && data.deleteReport) {
          setSuccessMessage(`Report ${reportId} successfully deleted.`);
          setReportId("");
          setIsDeleteConfirmOpen(false);
        } else {
          setErrorMessage("Report not found. The report ID may be incorrect or the report may have already been deleted.");
          setIsDeleteConfirmOpen(false);
        }
      },
      onError: (error) => {
        const errorMsg = error.message || "Unknown error occurred";
        if (errorMsg.includes("not found")) {
          setErrorMessage("Report not found. The report ID may be incorrect or the report may have already been deleted.");
        } else {
          setErrorMessage(`Error deleting report: ${errorMsg}`);
        }
        setIsDeleteConfirmOpen(false);
      }
    });
  };

  return (
    <Segment basic>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          if (uuidValidate(reportId.trim())) {
            setIsDeleteConfirmOpen(true);
          }
        }}
      >
        <Header as="h3">
          Delete Report
          <Header.Subheader>
            Permanently delete a report by ID
          </Header.Subheader>
        </Header>

        {successMessage && (
          <Message positive>
            <Message.Header>Success</Message.Header>
            <p>{successMessage}</p>
          </Message>
        )}

        {errorMessage && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{errorMessage}</p>
          </Message>
        )}

        <Form.Input
          label="Report ID"
          value={reportId}
          placeholder="Enter report ID"
          onChange={(e, { value }) => setReportId(value)}
        />

        <Form.Button
          loading={loading}
          disabled={!uuidValidate(reportId.trim())}
          type="submit"
        >
          Delete Report
        </Form.Button>
      </Form>

      <Confirm
        open={isDeleteConfirmOpen}
        onCancel={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        confirmButton="Delete Report"
        content={`Are you sure you want to permanently delete report ${reportId}? This action cannot be undone.`}
        header="Delete Report"
      />
    </Segment>
  );
};

const AdminHome = () => {
  return (
    <div>
      <Breadcrumb size="tiny">
        <Breadcrumb.Section>
          <Link to="/admin">Admin Home</Link>
        </Breadcrumb.Section>
      </Breadcrumb>
      <UserSearchSection />
      <SubscriptionSearchSection />
      <UserInvitationSection />
      <DeleteReportSection />
      <NewSubscriptionSection />
    </div>
  );
};

export default AdminHome;
